/*
 * The sidebar bell. Opens the Notifications sheet; its badge (and the page
 * title's share) is my items overdue or due today plus unread mentions.
 */
import { Bell } from "lucide-react";
import { useCallback, useEffect, useMemo, useState, type FC } from "react";

import {
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem
} from "@/components/ui/sidebar";
import { useAgentsTrayStore } from "@/features/agentAttention/context/agentsTrayStore";
import { setTitleCount } from "@/lib/documentTitle";

import { SettingsModal } from "../../components/settings/SettingsModal";
import { useComingUp } from "./comingUp/useComingUp";
import { bellCount } from "./feedModel";
import { useNotifications } from "./NotificationsProvider";
import { NotificationsSheet } from "./NotificationsSheet";

export const NOTIFICATIONS_TITLE_SOURCE = "notifications";

const capLabel = (c: number) => (c > 99 ? "99+" : String(c));

export const NotificationsBell: FC = () => {
  const { items, markSeen } = useNotifications();
  const mine = useComingUp("mine");
  const setSheetOpen = useAgentsTrayStore((s) => s.setSheetOpen);
  const [open, setOpen] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const count = useMemo(
    () => bellCount(mine.items, items),
    [mine.items, items]
  );

  useEffect(() => {
    setTitleCount(NOTIFICATIONS_TITLE_SOURCE, count);
  }, [count]);
  useEffect(() => () => setTitleCount(NOTIFICATIONS_TITLE_SOURCE, 0), []);

  const onOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next);
      setSheetOpen(next);
      if (next) void markSeen();
    },
    [markSeen, setSheetOpen]
  );

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        tooltip="Notifications"
        isActive={open}
        data-testid="notifications-bell"
        aria-label={
          count > 0 ? `Notifications (${capLabel(count)})` : "Notifications"
        }
        onClick={() => onOpenChange(!open)}
      >
        <span className="relative inline-flex shrink-0">
          <Bell className="size-4" />
          {count > 0 ? (
            <span
              aria-hidden
              data-testid="notifications-unseen-dot"
              className="absolute -top-0.5 -right-0.5 size-2 rounded-full bg-primary ring-1 ring-sidebar"
            />
          ) : null}
        </span>
        <span className="truncate">Notifications</span>
      </SidebarMenuButton>
      {count > 0 ? (
        <SidebarMenuBadge
          className="bg-primary/10 text-primary"
          data-testid="notifications-badge"
        >
          {capLabel(count)}
        </SidebarMenuBadge>
      ) : null}
      <NotificationsSheet
        open={open}
        onOpenChange={onOpenChange}
        mine={mine}
        onOpenPreferences={() => setPrefsOpen(true)}
      />
      {prefsOpen ? (
        <SettingsModal
          open={prefsOpen}
          onOpenChange={setPrefsOpen}
          initialSection="notifications"
        />
      ) : null}
    </SidebarMenuItem>
  );
};
